import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CareerTimeline from './CareerTimeline';
import '../styles/AboutMe.css';
import chanduImage from '../assets/chandu.png';

// MUI Icons
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import FacebookIcon from '@mui/icons-material/Facebook';
import ScienceIcon from '@mui/icons-material/Science';
import DownloadIcon from '@mui/icons-material/Download';
import Tooltip from '@mui/material/Tooltip';

const AboutMe: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'About Chandra Mouli Yalamanchili | Mainframe Modernization Architect';

    const setMetaTag = (attr: 'name' | 'property', key: string, content: string) => {
      let element = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attr, key);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    const setCanonical = (href: string) => {
      let link = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };

    const setOrCreateScript = (id: string, json: Record<string, unknown>) => {
      let script = document.getElementById(id) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = id;
        document.head.appendChild(script);
      }
      script.text = JSON.stringify(json);
    };

    const pageTitle = 'About Chandra Mouli Yalamanchili | Mainframe Modernization Architect';
    const pageDescription =
      'Lead Software Architect with 18+ years of experience in IBM mainframe modernization, distributed systems, hybrid architecture, and mission-critical financial platforms.';
    const pageUrl = 'https://chandramouli.info/about';
    const imageUrl = 'https://chandramouli.info/og.png';

    setMetaTag('name', 'description', pageDescription);
    setCanonical(pageUrl);

    setMetaTag('property', 'og:title', pageTitle);
    setMetaTag('property', 'og:description', pageDescription);
    setMetaTag('property', 'og:url', pageUrl);
    setMetaTag('property', 'og:type', 'profile');
    setMetaTag('property', 'og:image', imageUrl);

    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'twitter:title', pageTitle);
    setMetaTag('name', 'twitter:description', pageDescription);
    setMetaTag('name', 'twitter:image', imageUrl);

    setOrCreateScript('about-page-schema', {
      '@context': 'https://schema.org',
      '@type': 'ProfilePage',
      mainEntity: {
        '@type': 'Person',
        '@id': 'https://chandramouli.info/#person',
        name: 'Chandra Mouli Yalamanchili',
        url: 'https://chandramouli.info/',
        image: imageUrl,
        jobTitle: 'Lead Software Architect',
        sameAs: [
          'https://www.linkedin.com/in/chandu85/',
          'https://www.researchgate.net/profile/Chandra-Mouli-Yalamanchili',
          'https://scholar.google.com/citations?user=JIj6SwoAAAAJ&hl=en'
        ],
        knowsAbout: [
          'IBM Mainframe',
          'Mainframe Modernization',
          'CICS',
          'HLASM',
          'Java on z/OS',
          'Distributed Systems',
          'Hybrid Cloud',
          'FinTech',
          'Real-Time Payment Systems',
          'Artificial Intelligence'
        ]
      }
    });

    return () => {
      document.title =
        'Chandra Mouli Yalamanchili | Mainframe Modernization & Java on z/OS Architect';
      setCanonical('https://chandramouli.info/');
      const script = document.getElementById('about-page-schema');
      if (script) script.remove();
    };
  }, []);

  return (
    <div className="aboutme-container">
      <section className="aboutme-hero">
        <div className="aboutme-header">
          <img
            src={chanduImage}
            alt="Chandra Mouli Yalamanchili"
            className="aboutme-profile-image"
          />

          <div className="aboutme-intro">
            <p className="aboutme-eyebrow">ABOUT ME</p>
            <h1 className="aboutme-title">Chandra Mouli Yalamanchili</h1>
            <p className="aboutme-subtitle">
              Lead Software Architect | Mainframe Modernization | Distributed Systems | AI Exploration
            </p>

            <p className="aboutme-also-known">
              Also known professionally as <strong>Chandu Yalamanchili</strong> and <strong>Chandramouli Yalamanchili</strong>.
            </p>

            <p className="aboutme-bio">
              I am a technology leader with over 18 years of experience building, modernizing,
              and scaling mission-critical financial systems. My career began with deep hands-on
              work in IBM mainframe technologies including assembler, COBOL, CICS, DB2, MQ, and
              transaction processing, and it has evolved into leading modernization efforts that
              connect proven legacy platforms with modern distributed technologies.
            </p>

            <p className="aboutme-bio">
              My work has contributed to transforming legacy authorization platforms into modern,
              API-driven systems capable of supporting large-scale transaction volumes with high availability.
              This transformation has been enabled through API-first integration, Java on z/OS, hybrid
              architecture patterns, and enterprise-scale engineering practices that preserve platform stability
              while expanding capability across both mainframe and distributed environments.
            </p>

            <p className="aboutme-bio">
              Across my career, I have served in roles including software developer, technical lead,
              architect, mentor, and modernization leader. That progression has
              given me both low-level technical depth and broad architectural perspective. I continue
              to operate deeply in the mainframe space while exploring modern software development,
              hybrid cloud models, and practical AI applications for enterprise engineering.
            </p>

            <p className="aboutme-impact">
              My work focuses on evolving legacy mainframe-based authorization systems into modern,
              API-driven platforms that support large-scale real-time financial transactions while preserving
              the stability and resilience required for mission-critical operations.
            </p>
          </div>
        </div>
      </section>

      <section className="aboutme-section">
        <h2>Professional Highlights</h2>
        <div className="aboutme-highlight-grid">
          <div className="aboutme-card">
            <h3>18+ Years of Experience in Enterprise Financial Systems</h3>
            <p>
              Extensive experience across IBM mainframe engineering, distributed systems,
              modernization strategy, and enterprise architecture.
            </p>
          </div>

          <div className="aboutme-card">
            <h3>Mainframe and Modern Engineering</h3>
            <p>
              Strong expertise across both legacy transaction platforms and modern integration
              approaches, including Java, APIs, platform modernization, and hybrid systems design.
            </p>
          </div>

          <div className="aboutme-card">
            <h3>High-Scale Financial Platforms (Millions of Transactions Daily)</h3>
            <p>
              Experience supporting high-throughput financial environments that demand strong
              availability, consistent performance, and operational resilience.
            </p>
          </div>

          <div className="aboutme-card">
            <h3>Architectural Leadership and Platform Transformation</h3>
            <p>
              Led modernization initiatives, guided engineering teams, and drove architectural
              evolution across enterprise-scale financial systems while preserving the stability
              expected from mission-critical platforms.
            </p>
          </div>
        </div>
      </section>

      <section className="aboutme-section">
        <h2>What I Focus On</h2>
        <div className="aboutme-two-column">
          <div className="aboutme-card">
            <h3>Mainframe Modernization</h3>
            <p>
              I focus on modernizing critical mainframe environments in ways that respect the
              strengths of those platforms: reliability, transactional integrity, scale, and
              business continuity. My approach is centered on practical modernization that adds
              flexibility without weakening operational discipline.
            </p>
          </div>

          <div className="aboutme-card">
            <h3>Hybrid Architecture</h3>
            <p>
              I design systems that bridge mainframe and distributed worlds. That includes
              integrating APIs into established transaction flows, enabling service-based
              interactions, and designing architecture patterns that connect core systems with modern
              platforms.
            </p>
          </div>

          <div className="aboutme-card">
            <h3>Engineering at Scale</h3>
            <p>
              My work consistently involves environments where design decisions have real operational
              and business consequences. I enjoy solving hard engineering problems in systems where
              throughput, latency, resilience, and correctness all matter at the same time.
            </p>
          </div>

          <div className="aboutme-card">
            <h3>AI in Enterprise Development</h3>
            <p>
              In addition to architecture and modernization, I actively explore AI-assisted
              development, intelligent engineering workflows, and practical ways AI can support
              knowledge discovery, developer productivity, and operational visibility in complex
              enterprise systems.
            </p>
          </div>
        </div>
      </section>

      <section className="aboutme-section">
        <h2>Core Expertise</h2>
        <div className="aboutme-tags">
          <span>IBM Mainframe</span>
          <span>HLASM / Assembler</span>
          <span>COBOL</span>
          <span>CICS</span>
          <span>DB2</span>
          <span>MQ</span>
          <span>z/OS</span>
          <span>Java on z/OS</span>
          <span>Distributed Systems</span>
          <span>API Integration</span>
          <span>OpenShift</span>
          <span>Kubernetes</span>
          <span>Real-Time Payments</span>
          <span>Authorization Systems</span>
          <span>Hybrid Architecture</span>
          <span>Mainframe Modernization</span>
          <span>AI Exploration</span>
        </div>
      </section>

      <section className="aboutme-section">
        <h2>Professional Memberships and Recognition</h2>
        <p className="aboutme-membership-intro">
          My professional memberships reflect continued engagement with the broader engineering,
          research, and technology community.
        </p>
        <div className="aboutme-list-card">
          <ul>
            <li>IEEE Senior Member</li>
            <li>Sigma Xi - Full Member</li>
            <li>BCS, Chartered Institute for IT - Fellow Member</li>
          </ul>
        </div>
      </section>

      <section className="aboutme-section">
        <h2>Research and Professional Profiles</h2>
        <p className="aboutme-research-intro">
          My research work focuses on mainframe modernization, enterprise architecture,
          and emerging technologies, and is available through the platforms below.
        </p>
        <div className="aboutme-list-card">
          <ul>
            <li>
              <a
                href="https://www.researchgate.net/profile/Chandra-Mouli-Yalamanchili"
                target="_blank"
                rel="noopener noreferrer"
              >
                ResearchGate
              </a>
            </li>
            <li>
              <a
                href="https://scholar.google.com/citations?user=JIj6SwoAAAAJ&hl=en"
                target="_blank"
                rel="noopener noreferrer"
              >
                Google Scholar
              </a>
            </li>
            <li>
              <a
                href="https://zenodo.org/search?q=metadata.creators.person_or_org.name%3A%28%22Yalamanchili%2C%20Chandra%20mouli%22%20%22Chandra%20mouli%20Yalamanchili%22%29&l=list&p=1&s=10&sort=bestmatch"
                target="_blank"
                rel="noopener noreferrer"
              >
                Zenodo
              </a>
            </li>
            <li>
              <a
                href="https://www.linkedin.com/in/chandu85/"
                target="_blank"
                rel="noopener noreferrer"
              >
                LinkedIn
              </a>
            </li>
            <li>
              <a href="/Chandramouli_Yalamanchili.pdf" download>
                Download Resume
              </a>
            </li>
          </ul>
          <p className="aboutme-note">
              My research reflects my independent scholarly work and is not affiliated with any employer or academic institution unless explicitly stated.
          </p>
        </div>
      </section>

      <section className="aboutme-section">
        <h2>Career Journey</h2>
        <p className="aboutme-section-intro">
          My career reflects a progression from hands-on systems engineering to architectural leadership,
          with a consistent focus on modernization, scalability, and enterprise impact.
        </p>
        <CareerTimeline />
      </section>

      <div className="aboutme-button-container">
        <button
          className="aboutme-button"
          onClick={() => navigate('/', { state: { fromAboutPage: true } })}
        >
          ← Back to Home
        </button>
      </div>

      <div className="aboutme-social-icons">
        <Tooltip title="LinkedIn">
          <a
            aria-label="LinkedIn"
            href="https://www.linkedin.com/in/chandu85/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <LinkedInIcon fontSize="medium" />
          </a>
        </Tooltip>

        <Tooltip title="ResearchGate">
          <a
            aria-label="ResearchGate"
            href="https://www.researchgate.net/profile/Chandra-Mouli-Yalamanchili"
            target="_blank"
            rel="noopener noreferrer"
          >
            <ScienceIcon fontSize="medium" />
          </a>
        </Tooltip>

        <Tooltip title="Facebook">
          <a
            aria-label="Facebook"
            href="https://www.facebook.com/chandu.yalamanchili/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FacebookIcon fontSize="medium" />
          </a>
        </Tooltip>

        <Tooltip title="Download Resume">
          <a aria-label="Download Resume" href="/Chandramouli_Yalamanchili.pdf" download>
            <DownloadIcon fontSize="medium" />
          </a>
        </Tooltip>
      </div>
    </div>
  );
};

export default AboutMe;